import { createApiRef } from '@backstage/core-plugin-api';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PersistedSessionState {
  sessionId: string;
  workspaceId: number;
  folderName: string;
  vncPath: string | null;
  slotNum: number | null;
  isOwnerOfActive: boolean;
  isPublic: boolean;
}

export type MatlabVncSessionListener = (
  sessions: PersistedSessionState[],
) => void;

export interface MatlabVncSessionApi {
  /** Returns the first minimized session, or null if none. (Backward compat) */
  getSession(): PersistedSessionState | null;
  /** Persist a new session state (pass null to clear). (Backward compat, adds single session) */
  setSession(state: PersistedSessionState | null): void;
  /** Add or update a session by sessionId. */
  addSession(state: PersistedSessionState): void;
  /** Remove a session by sessionId. */
  removeSession(sessionId: string): void;
  /** Get all minimized sessions as an array. */
  getAllSessions(): PersistedSessionState[];
  /** Subscribe to any session changes. Returns an unsubscribe function. */
  subscribe(listener: MatlabVncSessionListener): () => void;
  /**
   * Start a background heartbeat for all active sessions.
   * Safe to call multiple times — only one timer runs at a time.
   * getToken is called on every tick so the token is always fresh.
   */
  startHeartbeat(baseUrl: string, getToken: () => Promise<string | undefined>): void;
  /** Stop the background heartbeat. */
  stopHeartbeat(): void;
  /**
   * Call the end-session endpoint for a specific session, and remove it from tracking.
   * The floating card calls this with baseUrl and token from Backstage APIs.
   */
  endSession(comment: string, baseUrl: string, token: string | undefined, sessionId: string): Promise<void>;
}

// ── API ref ───────────────────────────────────────────────────────────────────

export const matlabVncSessionApiRef = createApiRef<MatlabVncSessionApi>({
  id: 'plugin.matlab-vnc.session',
});

// ── Implementation ────────────────────────────────────────────────────────────

const STORAGE_KEY = 'code4ev.matlabVnc.minimizedSessions';
const BROADCAST_CHANNEL_NAME = 'code4ev.matlabVnc.sessions';

export class MatlabVncSessionClient implements MatlabVncSessionApi {
  private sessions = new Map<string, PersistedSessionState>();
  private listeners = new Set<MatlabVncSessionListener>();
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatBaseUrl = '';
  private heartbeatGetToken: (() => Promise<string | undefined>) | null = null;
  private broadcastChannel: BroadcastChannel | null = null;

  constructor() {
    // Restore from sessionStorage on initialisation (handles page refresh)
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, PersistedSessionState>;
        for (const [sessionId, state] of Object.entries(parsed)) {
          if (state?.sessionId && state?.workspaceId && state?.folderName) {
            this.sessions.set(sessionId, state);
          }
        }
      }
    } catch {
      // ignore malformed JSON
    }

    // BroadcastChannel keeps all open tabs in sync
    try {
      this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      this.broadcastChannel.onmessage = (e: MessageEvent) => {
        if (e.data?.type === 'SESSIONS_UPDATE') {
          this.sessions.clear();
          const sessionsData = e.data.sessions as Record<string, PersistedSessionState>;
          for (const [sessionId, state] of Object.entries(sessionsData || {})) {
            if (state?.sessionId && state?.workspaceId && state?.folderName) {
              this.sessions.set(sessionId, state);
            }
          }
          this.persistToStorage();
          this.notify();
        }
      };
    } catch {
      // BroadcastChannel unavailable — graceful degradation
    }
  }

  getSession(): PersistedSessionState | null {
    // Return the first session (backward compat)
    for (const session of this.sessions.values()) {
      return session;
    }
    return null;
  }

  setSession(state: PersistedSessionState | null): void {
    // Backward compat: clear all and add one
    this.sessions.clear();
    if (state) {
      this.sessions.set(state.sessionId, state);
    }
    this.persistToStorage();
    if (this.sessions.size === 0) this.stopHeartbeat();
    try {
      this.broadcastChannel?.postMessage({
        type: 'SESSIONS_UPDATE',
        sessions: Object.fromEntries(this.sessions),
      });
    } catch {
      // ignore
    }
    this.notify();
  }

  addSession(state: PersistedSessionState): void {
    this.sessions.set(state.sessionId, state);
    this.persistToStorage();
    try {
      this.broadcastChannel?.postMessage({
        type: 'SESSIONS_UPDATE',
        sessions: Object.fromEntries(this.sessions),
      });
    } catch {
      // ignore
    }
    this.notify();
  }

  removeSession(sessionId: string): void {
    this.sessions.delete(sessionId);
    this.persistToStorage();
    if (this.sessions.size === 0) this.stopHeartbeat();
    try {
      this.broadcastChannel?.postMessage({
        type: 'SESSIONS_UPDATE',
        sessions: Object.fromEntries(this.sessions),
      });
    } catch {
      // ignore
    }
    this.notify();
  }

  getAllSessions(): PersistedSessionState[] {
    return Array.from(this.sessions.values());
  }

  subscribe(listener: MatlabVncSessionListener): () => void {
    this.listeners.add(listener);
    // Notify immediately with current sessions so callers don't need a
    // separate getAllSessions() call.
    listener(this.getAllSessions());
    return () => this.listeners.delete(listener);
  }

  startHeartbeat(baseUrl: string, getToken: () => Promise<string | undefined>): void {
    this.heartbeatBaseUrl = baseUrl;
    this.heartbeatGetToken = getToken;
    if (this.heartbeatTimer) return; // already running — getToken reference updated above
    this.heartbeatTimer = setInterval(async () => {
      if (!this.heartbeatBaseUrl) return;
      const token = this.heartbeatGetToken ? await this.heartbeatGetToken().catch(() => undefined) : undefined;
      for (const session of this.sessions.values()) {
        const sid = session.sessionId;
        if (!sid) continue;
        try {
          const resp = await fetch(`${this.heartbeatBaseUrl}/heartbeat/${sid}`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          // 404 means the backend killed the session (timeout/restart) — remove it
          if (resp.status === 404) {
            this.removeSession(sid);
          }
        } catch {
          // network errors are non-fatal — server has its own timeout
        }
      }
    }, 10_000);
  }

  stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  async endSession(comment: string, baseUrl: string, token: string | undefined, sessionId: string): Promise<void> {
    const session = this.sessions.get(sessionId);
    if (!session || !baseUrl) return;
    try {
      await fetch(`${baseUrl}/workspaces/end-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          folder_name: session.folderName,
          workspace_id: session.workspaceId,
          comment: comment.trim(),
        }),
      });
    } finally {
      // Always remove session even if the network call fails
      this.removeSession(sessionId);
    }
  }

  private persistToStorage(): void {
    try {
      const sessionsObj = Object.fromEntries(this.sessions);
      if (this.sessions.size > 0) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(sessionsObj));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // ignore storage quota errors
    }
  }

  private notify(): void {
    const sessions = this.getAllSessions();
    this.listeners.forEach(l => l(sessions));
  }
}
